// A copy of Standard's `src/feed/ResponseItem` scoring helpers, until both import one module.

export type SectionFormat = 'healthQuestionnaire' | 'singleChoice' | 'multipleChoice' | 'default'

const HEALTH_QUESTIONNAIRE_TYPES = new Set(['PHQ-9_QUESTIONNAIRE', 'GAD-7_QUESTIONNAIRE'])
const SINGLE_CHOICE_TYPES = new Set([
  'BURNOUT_QUESTIONNAIRE',
  'QUIZ_SINGLE_CHOICE',
  'CARD:QUESTION_SINGLE_CHOICE',
  'CARD:BUTTONS_LIST',
])
const MULTIPLE_CHOICE_TYPES = new Set([
  'THINKING_TRAP_SERIES',
  'QUIZ_MULTIPLE_CHOICE',
  'CARD:QUESTION_MULTIPLE_CHOICE',
  'CARD:QUESTIONS_WITH_CHECKBOX',
])

export const getSectionFormat = (type?: string | null): SectionFormat => {
  if (!type) {
    return 'default'
  }
  if (HEALTH_QUESTIONNAIRE_TYPES.has(type)) {
    return 'healthQuestionnaire'
  }
  if (SINGLE_CHOICE_TYPES.has(type)) {
    return 'singleChoice'
  }
  if (MULTIPLE_CHOICE_TYPES.has(type)) {
    return 'multipleChoice'
  }
  return 'default'
}

// Shown as the section's score, so left out of its entry list.
export const HQ_SCORE_TAGS = new Set(['PHQ9_SCORE', 'GAD7_SCORE'])

type ScoreSection = { type?: string | null; entries: Array<{ tag?: string | null; rawValue?: unknown }> }
type ScoredResponse = { sections: ScoreSection[] }

export type HealthQuestionnaireScore = { label: string; type: string; value: number }

const SCORE_SORT_ORDER = ['GAD-7_QUESTIONNAIRE', 'PHQ-9_QUESTIONNAIRE', 'BURNOUT_QUESTIONNAIRE']

const scoreEntryValue = (section: ScoreSection, tag: string): number | null => {
  const value = Number(section.entries.find((entry) => entry.tag === tag)?.rawValue)
  return Number.isNaN(value) ? null : value
}

// Burnout has no score entry: its score is the 1-based position of the chosen option.
const burnoutScore = (section: ScoreSection): number | null => {
  const index = section.entries.findIndex((entry) => entry.rawValue === true)
  return index < 0 ? null : index + 1
}

const sectionScore = (section: ScoreSection): HealthQuestionnaireScore | null => {
  if (section.type === 'BURNOUT_QUESTIONNAIRE') {
    const value = burnoutScore(section)
    return value === null ? null : { label: 'Burnout', type: section.type, value }
  }
  if (section.type === 'GAD-7_QUESTIONNAIRE') {
    const value = scoreEntryValue(section, 'GAD7_SCORE')
    return value === null ? null : { label: 'Anxiety', type: section.type, value }
  }
  if (section.type === 'PHQ-9_QUESTIONNAIRE') {
    const value = scoreEntryValue(section, 'PHQ9_SCORE')
    return value === null ? null : { label: 'Depression', type: section.type, value }
  }
  return null
}

export const getHealthQuestionnaireScores = (response: ScoredResponse): HealthQuestionnaireScore[] =>
  response.sections
    .map(sectionScore)
    .filter((score): score is HealthQuestionnaireScore => score !== null)
    .sort((a, b) => SCORE_SORT_ORDER.indexOf(a.type) - SCORE_SORT_ORDER.indexOf(b.type))

const HRV_DURATION_TAGS = new Set(['HRV_BIOFEEDBACK_METRICS', 'HRV_RESTING_METRICS'])

// The logged practice length, preferred over the lesson's prescribed `duration`.
export const getMeditationMinutes = (response: ScoredResponse): number | null => {
  const practiceSection = response.sections.find((section) => section.type === 'CARD:HRV_PRACTICE')
  const entry = practiceSection?.entries.find((candidate) => HRV_DURATION_TAGS.has(candidate.tag ?? ''))
  if (!entry?.rawValue) {
    return null
  }
  const { durationInSec } = entry.rawValue as { durationInSec?: number }

  return durationInSec === undefined ? null : Math.round(durationInSec / 60)
}
